import { TFunction } from 'i18next';
import {
  Operation,
  OperationDirection,
  RefundDetails,
  TransactionTabName,
} from '@/features/Profile/sections/Transactions/Transactions.types';
import { PaymentMethod } from '@/api/types/payments.types';
import { getPaymentMethodLabel } from '@/components/Payment/PaymentMethods.helpers';
import {
  OperationsConnection,
  OperationsResponse,
  PaymentAd,
  UserPaidAdsTransaction,
  UserPayment,
  UserTransaction,
} from '@/features/Profile/sections/Transactions/api';
import { formatOperationAmount } from '@/features/Profile/sections/Transactions/Transactions.helpers';
import { formatDate } from '@/utils/helpers';

const REFUND_SERVICE_ID = 'refund';
const EMPTY_OPERATIONS = { operations: [], pageInfo: undefined };

function getOperationDirection(amount: number): OperationDirection {
  return amount >= 0 ? OperationDirection.Income : OperationDirection.Expense;
}

function resolvePaymentMethodLabel(
  t: TFunction,
  paymentMethod?: string | null
): string | undefined {
  if (!paymentMethod) {
    return undefined;
  }

  const normalized = paymentMethod.toUpperCase() as PaymentMethod;

  if (normalized === PaymentMethod.Reversal) {
    return 'Reversal';
  }

  if (normalized === PaymentMethod.Reimbursement) {
    return 'Reimbursement';
  }

  return getPaymentMethodLabel(t, normalized, undefined, true) || paymentMethod;
}

function getNodeId(node: {
  paymentLegacyId?: number | null;
  serviceId: string;
  date: string;
  amount: number;
}): string {
  return (
    node.paymentLegacyId?.toString() ??
    `${node.serviceId}-${node.date}-${node.amount}`
  );
}

function mapAd(ad: PaymentAd | null) {
  if (!ad) {
    return undefined;
  }

  const { photo, ...rest } = ad;
  return { ...rest, image: photo?.url ?? null };
}

function buildOperation(
  base: {
    paymentLegacyId?: number | null;
    id?: string;
    operationNumber?: string;
    serviceId: string;
    displayServiceName: string;
    amount: number;
    paymentMethod?: string | null;
    date: string;
    isRefund?: boolean;
    isPaidAds?: boolean;
    refund?: RefundDetails;
    invoiceId?: string;
    invoiceUrl?: string;
  },
  direction: OperationDirection,
  ad: PaymentAd | null,
  t?: TFunction
): Operation {
  const operationNumber =
    base.operationNumber ?? base.paymentLegacyId?.toString() ?? '-';
  const absAmount = Math.abs(base.amount);
  const paymentMethodLabel = t
    ? resolvePaymentMethodLabel(t, base.paymentMethod)
    : undefined;
  return {
    id:
      base.id ??
      getNodeId({
        paymentLegacyId: base.paymentLegacyId,
        serviceId: base.serviceId,
        date: base.date,
        amount: base.amount,
      }),
    serviceId: base.serviceId,
    operationNumber,
    serviceName: base.displayServiceName,
    amount: absAmount,
    formattedAmount: formatOperationAmount(
      absAmount,
      direction,
      base.isPaidAds ? undefined : '₼'
    ),
    paymentMethod: base.paymentMethod ?? undefined,
    paymentMethodLabel,
    date: formatDate(base.date, 'dd.MM.yyyy, HH:mm'),
    direction,
    isIncome: direction === OperationDirection.Income,
    isRefund: base.isRefund ?? false,
    isPaidAds: base.isPaidAds ?? false,
    ad: mapAd(ad),
    refund: base.refund,
    invoiceId: base.invoiceId,
    invoiceUrl: base.invoiceUrl,
  };
}

function mapTransactionToOperation(
  {
    paymentLegacyId,
    serviceId,
    displayServiceName,
    amount,
    paymentMethod,
    createdAt,
    ad,
  }: UserTransaction,
  t: TFunction
): Operation {
  return buildOperation(
    {
      paymentLegacyId,
      serviceId,
      displayServiceName,
      amount,
      paymentMethod,
      date: createdAt,
    },
    getOperationDirection(amount),
    ad,
    t
  );
}

function mapRefundDetails(
  payment: UserPayment,
  t: TFunction
): RefundDetails | undefined {
  const original = payment.originalPayment;

  if (!original) {
    return undefined;
  }

  return {
    paidServiceName: original.displayServiceName,
    paidPaymentMethodLabel: resolvePaymentMethodLabel(
      t,
      original.paymentMethod
    ),
    adNumber: original.ad?.legacyResourceId?.toString(),
    rrn: original.rrn ?? undefined,
  };
}

function mapPaymentToOperation(payment: UserPayment, t: TFunction): Operation {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { originalPayment, updatedAt, ad, ...rest } = payment;
  const isRefund = payment.serviceId === REFUND_SERVICE_ID;
  const direction = isRefund
    ? OperationDirection.Income
    : OperationDirection.Expense;
  return buildOperation(
    {
      ...rest,
      date: updatedAt,
      isRefund,
      refund: mapRefundDetails(payment, t),
    },
    direction,
    payment.ad,
    t
  );
}

function mapPaidAdsTransactionToOperation({
  paidAdsTransactionLegacyId,
  invoiceLegacyId,
  displayServiceName,
  amount,
  createdAt,
  ad,
}: UserPaidAdsTransaction): Operation {
  const invoiceId = invoiceLegacyId ?? undefined;
  return buildOperation(
    {
      id: paidAdsTransactionLegacyId,
      operationNumber: paidAdsTransactionLegacyId,
      serviceId: 'paid-ads',
      displayServiceName,
      amount,
      date: createdAt,
      isPaidAds: true,
      invoiceId,
      invoiceUrl: invoiceId ? `/invoices/${invoiceId}/print` : undefined,
    },
    getOperationDirection(amount),
    ad
  );
}

function mapOperationsConnection<Node>(
  connection: OperationsConnection<Node>,
  mapNode: (node: Node) => Operation
) {
  return {
    operations: connection.nodes.map(mapNode),
    pageInfo: connection.pageInfo,
  };
}

export function getOperationsAndPageInfo(
  tab: TransactionTabName,
  data: OperationsResponse | undefined,
  t: TFunction
) {
  if (!data) {
    return EMPTY_OPERATIONS;
  }

  if (tab === TransactionTabName.Account && 'currentUserTransactions' in data) {
    return mapOperationsConnection(data.currentUserTransactions, transaction =>
      mapTransactionToOperation(transaction, t)
    );
  }

  if (tab === TransactionTabName.Ads && 'currentUserPayments' in data) {
    return mapOperationsConnection(data.currentUserPayments, payment =>
      mapPaymentToOperation(payment, t)
    );
  }

  if (
    tab === TransactionTabName.PaidAds &&
    'currentUserPaidAdsTransactions' in data
  ) {
    return mapOperationsConnection(
      data.currentUserPaidAdsTransactions,
      mapPaidAdsTransactionToOperation
    );
  }

  return EMPTY_OPERATIONS;
}

function mergeNodes<Node>(
  previous: Node[],
  incoming: Node[],
  getId: (node: Node) => string
): Node[] {
  const existingIds = new Set(previous.map(getId));
  return [
    ...previous,
    ...incoming.filter(node => !existingIds.has(getId(node))),
  ];
}

function mergeOperationsConnection<Node>(
  previous: OperationsConnection<Node>,
  incoming: OperationsConnection<Node>,
  getId: (node: Node) => string
): OperationsConnection<Node> {
  if (incoming.nodes.length === 0) {
    return previous;
  }

  return {
    ...previous,
    nodes: mergeNodes(previous.nodes, incoming.nodes, getId),
    pageInfo: incoming.pageInfo,
    totalCount: incoming.totalCount,
  };
}

export function mergeOperationsResults(
  tab: TransactionTabName,
  prevResult: OperationsResponse,
  { fetchMoreResult }: { fetchMoreResult?: OperationsResponse }
): OperationsResponse {
  if (!fetchMoreResult) {
    return prevResult;
  }

  if (
    tab === TransactionTabName.Account &&
    'currentUserTransactions' in prevResult &&
    'currentUserTransactions' in fetchMoreResult
  ) {
    return {
      ...prevResult,
      currentUserTransactions: mergeOperationsConnection(
        prevResult.currentUserTransactions,
        fetchMoreResult.currentUserTransactions,
        ({ createdAt, ...rest }) => getNodeId({ ...rest, date: createdAt })
      ),
    };
  }

  if (
    tab === TransactionTabName.Ads &&
    'currentUserPayments' in prevResult &&
    'currentUserPayments' in fetchMoreResult
  ) {
    return {
      ...prevResult,
      currentUserPayments: mergeOperationsConnection(
        prevResult.currentUserPayments,
        fetchMoreResult.currentUserPayments,
        ({ updatedAt, ...rest }) => getNodeId({ ...rest, date: updatedAt })
      ),
    };
  }

  if (
    tab === TransactionTabName.PaidAds &&
    'currentUserPaidAdsTransactions' in prevResult &&
    'currentUserPaidAdsTransactions' in fetchMoreResult
  ) {
    return {
      ...prevResult,
      currentUserPaidAdsTransactions: mergeOperationsConnection(
        prevResult.currentUserPaidAdsTransactions,
        fetchMoreResult.currentUserPaidAdsTransactions,
        transaction => transaction.paidAdsTransactionLegacyId
      ),
    };
  }

  return prevResult;
}
